import Phaser from 'phaser';
import { speakRoundIntro } from '@/game/roundIntro';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK, SURVIVAL } from '@/game/config';
import { MatchClass, type BoardRule } from '@/game/rules/BoardRule';
import { wordInClass, canBeDecoy } from '@/data/packs/membership';
import { survivalPack } from '@/game/packs';
import { wordTextStyle } from '@/game/fonts';
import { ReadingProgression } from '@/game/progression/ReadingProgression';
import { DRILL_CLASSES } from '@/game/progression/config';
import { getBackground, getBestSurvival, setBestSurvival } from '@/game/storage';
import { Starfield } from '@/game/objects/Starfield';
import { Crosshair } from '@/game/objects/Crosshair';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { emphasise } from '@/game/ui/promptEmphasis';
import { createSplitPromptBanner } from '@/game/ui/promptBanner';
import { markerForRule } from '@/game/ui/promptMarker';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { drawContour, hasContour } from '@/game/contours';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { maybeAskForRating } from '@/game/rating';
import { trackSurvivalStart, trackSurvivalEnd } from '@/analytics';
import {
  playPopSound,
  playRecordSound,
  playRoundCompleteSound,
  startBackdropAmbience,
  stopBackdropAmbience,
} from '@/game/audio';
import type { Pack, PackWord } from '@/data/packs/types';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';

interface SurvivalInit { lang: string }

/** Survival's exclusive backdrop — always shown here, independent of the user-selected background. */
const SURVIVAL_BG_KEY = 'bg_survival';
const SURVIVAL_BG_URL = '/images/survival_backdrop.jpg';

/** One falling word currently on screen. */
interface Faller {
  label: Phaser.GameObjects.Text;
  tween: Phaser.Tweens.Tween;
}

/**
 * Survival Escalation — the UFO reward minigame. Words fall from the top, faster and faster.
 * The player taps the ones matching the rule (e.g. "Smash all nouns!"). The run is endless until
 * the player taps a word that does NOT match — words that fall off the bottom just disappear.
 * Self-contained — it doesn't touch RoundScene/BoardScene/lives, and is launched by spending a UFO.
 */
export class SurvivalScene extends Phaser.Scene {
  private lang!: string;
  private rule!: BoardRule;
  private targets: PackWord[] = [];
  private decoys: PackWord[] = [];

  private cleared = 0;
  private best = 0;
  private over = false;

  private spawnMs = SURVIVAL.spawnStartMs;
  private lifetimeMs = SURVIVAL.lifetimeStartMs;

  private fallers = new Set<Faller>();
  private clearedText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'Survival' });
  }

  init(data: SurvivalInit): void {
    this.lang = data.lang;
    this.cleared = 0;
    this.over = false;
    this.spawnMs = SURVIVAL.spawnStartMs;
    this.lifetimeMs = SURVIVAL.lifetimeStartMs;
    this.fallers = new Set();
  }

  preload(): void {
    // Survival has its own exclusive backdrop (not the user-selected background).
    if (!this.textures.exists(SURVIVAL_BG_KEY)) {
      this.load.image(SURVIVAL_BG_KEY, SURVIVAL_BG_URL);
    }
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    this.best = getBestSurvival();
    trackSurvivalStart();   // web demo only; the UFO is earned at Mars, so a demo run can reach it

    // Backdrop + ambience, mirroring BoardScene so the mode feels of-a-piece.
    this.drawBackdrop();
    startBackdropAmbience(getBackground());
    document.body.classList.add('in-game');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      document.body.classList.remove('in-game');
      stopBackdropAmbience();
    });

    // Pick the rule + word pools from the ACTIVE lesson's journey pool (learned words first), so a
    // UFO earned in the Fables journey launches a fables Survival with the `creature` rule.
    // Banner-context lessons can't produce a Survival rule at all — survivalPack() swaps them for a
    // class-match lesson.
    const pool = survivalPack();
    this.buildPools(pool);

    this.drawHud();

    this.input.setDefaultCursor('none');
    new Crosshair(this);

    // Mute toggle (bottom-left, same spot as the menu/board). Survival plays the pop per-word, not
    // on empty taps, so no pop suppression is needed here — just start/stop the bed live.
    addMuteToggle(this, {
      depth: 50,
      onToggle: (muted) => {
        if (muted) stopBackdropAmbience();
        else startBackdropAmbience(getBackground());
      },
    });

    // Self-rescheduling spawn loop. Difficulty no longer ramps on a timer — it tightens a tiny bit
    // per correct smash (see onCorrect), so the run only speeds up as the player succeeds.
    this.scheduleSpawn();
  }

  // ── setup ──────────────────────────────────────────────────────────────────

  private drawBackdrop(): void {
    // Exclusive Survival backdrop (UFO tractor beam over a neon city). Starfield is the fallback
    // if the image hasn't loaded yet.
    if (this.textures.exists(SURVIVAL_BG_KEY)) {
      const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, SURVIVAL_BG_KEY);
      img.setScale(Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height));
      img.setDepth(-1);
    } else {
      new Starfield(this);
    }
  }

  private buildPools(pool: Pack): void {
    const words = new ReadingProgression(this.lang, pool).survivalWords();
    const drills = (pool.drillClasses && pool.drillClasses.length > 0) ? pool.drillClasses : DRILL_CLASSES;

    // Prefer a drill class that actually has words to fly. Journey pools always have one of the
    // standard drill classes; arcade packs (e.g. the Thai tones lesson) declare their own classes
    // (mid/low/…) that aren't in DRILL_CLASSES, so fall back to whatever the pack declares and has
    // words for — otherwise Survival would pick a non-existent class and spawn zero targets.
    const eligible = drills.filter((c) => words.some((w) => wordInClass(w, c)));
    const ownClasses = pool.classes.map((c) => c.id).filter((id) => words.some((w) => wordInClass(w, id)));
    const candidateIds = eligible.length > 0 ? eligible : (ownClasses.length > 0 ? ownClasses : drills);
    const classId = candidateIds[Math.floor(Math.random() * candidateIds.length)];

    // `found` is kept apart from the synthesized fallback below: the fallback's "label" is just the
    // class id, which is also its whole prompt, so handing it to emphasise() would set the entire
    // banner in bold rather than one word. No label → no emphasis → the plain banner.
    const found = pool.classes.find((c) => c.id === classId);
    const cls = found ?? { id: classId, label: classId, prompt: classId };
    this.rule = new MatchClass(cls);

    // Both pools stay in learned-first order; we take the most familiar slice of each. A creature
    // counts as a noun, so it's a target (not a decoy) on a Nomen run — wordInClass, not bare class.
    // Decoys go through canBeDecoy so the class's decoyExcludeClasses hold here too: a run ends on
    // the first wrong tap and there's no "?" button, so a genuinely confusable word (an apple on the
    // Natur run) must never fall. `cls` may be the synthesized fallback — it just has no exclusions.
    this.targets = words.filter((w) => wordInClass(w, classId)).slice(0, SURVIVAL.workingSetSize);
    this.decoys = words.filter((w) => canBeDecoy(w, cls)).slice(0, SURVIVAL.workingSetSize);

    // Rule banner (same look as BoardScene's prompt), pinned near the top. A tonal pack tucks its
    // pitch-contour arrow inside the pill after the text, mirroring BoardScene.
    const showContour = !!(this.rule.contour && hasContour(this.rule.contour));
    const ARROW_GAP = 12;
    const ARROW_W = 52;
    const extraRight = showContour ? ARROW_GAP + ARROW_W : 0;

    // The rule word gets picked out of the sentence, exactly as on a board: everything around it is
    // boilerplate ("Smash all …" is the same on every run), and here the player is re-reading it
    // while words are already falling. Phaser Text has no inline styling, so the emphasised banner
    // is three Texts in a row; when no key word can be identified it falls back to the plain Text.
    const keyWord = emphasise(this.rule.prompt, found?.label, found?.emphasis);
    const banner: Phaser.GameObjects.Text | Phaser.GameObjects.Container = keyWord
      ? createSplitPromptBanner(
        this, keyWord, GAME_WIDTH / 2 - extraRight / 2, 56, '34px',
        // As on a board: the "Der, Die, Das" run marks its article with the school symbol.
        markerForRule(pool.id, classId),
      )
      : this.add.text(GAME_WIDTH / 2 - extraRight / 2, 56, this.rule.prompt, {
        fontFamily: FONT_STACK,
        fontSize: '34px', color: '#ffd166', padding: { x: 18, y: 10 },
      }).setOrigin(0.5);
    // Survival has no rounds — one rule for the whole run — so its instruction is spoken once, as
    // the run opens. Round 1 is passed literally for that reason.
    speakRoundIntro(this.rule.prompt, this.lang, 1);

    // Before the pill: addRoundedLabelBg draws itself one step behind whatever depth it finds here.
    banner.setDepth(50);
    addRoundedLabelBg(this, banner, { padRight: extraRight });

    if (showContour) {
      const glyph = drawContour(this, this.rule.contour!);
      if (glyph) glyph.setPosition(banner.x + banner.width / 2 + extraRight / 2, 56).setDepth(50);
    }
  }

  private drawHud(): void {
    this.clearedText = this.add.text(GAME_WIDTH / 2, 110, this.clearedLabel(), {
      fontFamily: FONT_STACK, fontSize: '22px', color: '#dfe6ee',
    }).setOrigin(0.5).setDepth(50);

    const back = this.add.text(12, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK, fontSize: '27px', color: '#dfe6ee', padding: { x: 22, y: 14 },
    }).setOrigin(0, 0).setDepth(50);
    // Tap target larger than the pill — same reasoning as HUDScene: at FIT scale on a phone the
    // drawn pill is well under Apple's 44 pt minimum. Grown out to the canvas corner plus a small
    // margin right/below; it sits above the falling words (depth 50) exactly as the pill already did.
    back.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(
        -back.x, -back.y, back.width + back.x + 10, back.height + back.y + 10,
      ),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    // Soft grey pill behind it — the starfield makes plain text hard to read.
    addRoundedLabelBg(this, back);
    back.on('pointerover', () => back.setColor('#ffffff'));
    back.on('pointerout', () => back.setColor('#dfe6ee'));
    back.on('pointerdown', () => this.scene.start('Menu'));
  }

  private clearedLabel(): string {
    return i18next.t('survival.cleared', { n: this.cleared });
  }

  // ── spawn loop ───────────────────────────────────────────────────────────────

  private scheduleSpawn(): void {
    if (this.over) return;
    this.spawnWord();
    this.time.delayedCall(this.spawnMs, () => this.scheduleSpawn());
  }

  private spawnWord(): void {
    const isTarget = this.targets.length > 0 && Math.random() < SURVIVAL.targetRatio;
    const pool = isTarget ? this.targets : (this.decoys.length > 0 ? this.decoys : this.targets);
    if (pool.length === 0) return;
    const word = pool[Math.floor(Math.random() * pool.length)];
    const target = this.rule.matches(word);

    const MARGIN = 120;
    const x = Phaser.Math.Between(MARGIN, GAME_WIDTH - MARGIN);
    const label = this.add.text(x, -40, word.text, {
      fontFamily: FONT_STACK,
      fontSize: '40px', color: '#ffffff',
      ...wordTextStyle(i18next.language),
    }).setOrigin(0.5);

    const HIT_PAD = 18;
    // Padded hit rect grown another 5% (centered) — a bit more forgiving for taps on fast fallers.
    const hitW = (label.width + HIT_PAD * 2) * 1.05;
    const hitH = (label.height + HIT_PAD * 2) * 1.05;
    label.setInteractive(
      new Phaser.Geom.Rectangle((label.width - hitW) / 2, (label.height - hitH) / 2, hitW, hitH),
      Phaser.Geom.Rectangle.Contains,
    );
    label.input!.cursor = 'pointer';

    const tween = this.tweens.add({
      targets: label,
      y: GAME_HEIGHT + 60,
      duration: this.lifetimeMs,
      ease: 'Linear',
      onComplete: () => this.onEscaped(faller),
    });

    const faller: Faller = { label, tween };
    this.fallers.add(faller);

    label.on('pointerdown', () => {
      if (this.over) return;
      if (target) this.onCorrect(faller);
      else this.gameOver();
    });
  }

  private onCorrect(faller: Faller): void {
    this.fallers.delete(faller);
    faller.tween.stop();
    faller.label.disableInteractive();
    playPopSound();
    this.cleared += 1;
    this.clearedText.setText(this.clearedLabel());
    // Speed up a tiny bit with each correct smash (tightens toward the floors).
    this.spawnMs = Math.max(SURVIVAL.spawnMinMs, this.spawnMs * SURVIVAL.rampPerHit);
    this.lifetimeMs = Math.max(SURVIVAL.lifetimeMinMs, this.lifetimeMs * SURVIVAL.rampPerHit);
    this.tweens.add({
      targets: faller.label,
      alpha: 0, scale: 1.4, duration: 200,
      onComplete: () => faller.label.destroy(),
    });
  }

  /** A word reached the bottom: it just disappears. Only a wrong tap ends the run. */
  private onEscaped(faller: Faller): void {
    this.fallers.delete(faller);
    faller.label.destroy();
  }

  // ── game over ─────────────────────────────────────────────────────────────────

  private gameOver(): void {
    if (this.over) return;
    this.over = true;
    this.input.enabled = false;

    // Freeze everything still falling.
    for (const f of this.fallers) {
      f.tween.stop();
      f.label.disableInteractive();
    }

    trackSurvivalEnd(this.cleared);   // web demo only

    const isRecord = this.cleared > this.best;
    if (isRecord) { setBestSurvival(this.cleared); this.best = this.cleared; playRecordSound(); }
    else playRoundCompleteSound();

    const headline = isRecord ? i18next.t('survival.newBest') : i18next.t('survival.gameOver');
    const message = `${headline}\n${i18next.t('survival.cleared', { n: this.cleared })}`;
    const overlay = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
      fontFamily: FONT_STACK,
      fontSize: '60px',
      color: isRecord ? '#4ecdc4' : '#ffd166',
      padding: { x: 34, y: 20 },
      align: 'center',
    }).setOrigin(0.5).setAlpha(0).setScale(0.8).setDepth(100);
    const overlayBg = addRoundedLabelBg(this, overlay, { radius: 24 });
    overlayBg.setAlpha(0).setScale(0.8);

    const cont = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 130, i18next.t('survival.continue'), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 16, y: 9 },
    }).setOrigin(0.5).setDepth(100);
    addRoundedLabelBg(this, cont);

    this.tweens.add({ targets: [overlay, overlayBg], alpha: 1, scale: 1, duration: 300, ease: 'Back.easeOut' });

    // Tap anywhere to return — after a short guard so the fatal tap doesn't dismiss it.
    this.input.setDefaultCursor('default');
    this.time.delayedCall(700, () => {
      this.input.enabled = true;
      this.input.once('pointerdown', () => this.scene.start('Menu'));
    });

    // End-of-game asks (web: buy card, apps: rating). Both live in Phaser's DOM layer, above the
    // canvas, so a tap on the card doesn't reach the "tap anywhere to continue" handler above.
    maybeShowBuyPrompt(this, { delayMs: 1500 });
    maybeAskForRating(this, { kind: 'survival', cleared: this.cleared });
  }
}
